import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Switch,
  Snackbar,
  Alert, CircularProgress, Drawer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./EditCampaignPanel.scss";
import { initialMonthlyBudget } from "../../../pages/metaTravelAdsCampaignCreator/utils/initialState";
import MetaTravelAdsCampaignEditor from "../MetaTravelAdsCampaignEditor";
import {APP_META_TRAVEL_ADS_API_ENDPOINT} from "constants/api_urls";
import { useSelector } from "react-redux";
import axios from "axios";
import {getUserToken} from "../../../utils/functions/userToken";
import { enqueueSnackbar } from "notistack";
import useSaveCampaignSettings from "./hooks/useSaveCampaignSettings";
import useSaveAudience from "./hooks/useSaveAudience";
import useSaveAdsCreative from "./hooks/useSaveAdsCreative";
import {
  getSharedAudienceList,
  mapSharedAudienceListToOptions,
} from "../Utils/getSharedAudienceList";
import useMetaOptions from "../Utils/useMetaOptions";

const EditCampaignPanel = ({ campaignId, idCampaign, isOpen, onClose, onStatusChange }) => {
  const token = getUserToken();
  const effectiveCampaignId = campaignId ?? idCampaign;
  const { saveCampaignSettings } = useSaveCampaignSettings();
  const { saveAudience } = useSaveAudience();
  const { saveAdsCreative } = useSaveAdsCreative();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { hotel_id = [] } = useSelector(
    (state) => state?.account
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [toastType, setToastType] = useState("error");
  const [expandedPanel, setExpandedPanel] = useState("settings");
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [visitedPanels, setVisitedPanels] = useState(["settings"]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [targetValue, setTargetValue] = useState("");
  const [campaignBudget, setCampaignBudget] = useState("");
  const [biddingStrategy, setBiddingStrategy] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState(initialMonthlyBudget);
  const [dailyBudgetCap, setDailyBudgetCap] = useState(false);
  const [isCampaignPaused, setIsCampaignPaused] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState("Ã¢â€šÂ¬");
  const [clickThroughValue, setClickThroughValue] = useState(7);
  const [viewThroughValue, setViewThroughValue] = useState(1);
  // Nouvel ÃƒÂ©tat pour placements et cartes crÃƒÂ©atives Meta
  const [placementDetails, setPlacementDetails] = useState({ placement_platform: [], placement_device: [] });
  const [adsCreativeCards, setAdsCreativeCards] = useState([{ ad_card_id: 1 }]);
  // Etats Audiences (RETARGETING)
  const [ageRange, setAgeRange] = useState({ min: "", max: "" });
  const [selectedGender, setSelectedGender] = useState("ALL");
  const [isBookingWindowEnabled, setIsBookingWindowEnabled] = useState(false);
  const [bookingWindowRange, setBookingWindowRange] = useState({ booking_window_start: "", booking_window_end: "" });
  const [audienceRules, setAudienceRules] = useState([]);
  const [selectedMetaAudience, setSelectedMetaAudience] = useState(null);
  const [selectedIncludeAudiences, setSelectedIncludeAudiences] = useState([]);
  const [selectedExcludeAudiences, setSelectedExcludeAudiences] = useState([]);
  const [metaAudienceOptions, setMetaAudienceOptions] = useState([]);
  const [isLoadingMetaAudienceOptions, setIsLoadingMetaAudienceOptions] = useState(false);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [lockedHotels, setLockedHotels] = useState([]);

  const { isSharedAudienceEnabled, isMultiHotelEnabled } = useMetaOptions(isOpen ? hotel_id?.[0] : null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isMounted = true;
    setIsLoadingMetaAudienceOptions(true);
    getSharedAudienceList(hotel_id?.[0] ? { hotel_id: hotel_id[0] } : {})
      .then((res) => {
        if (!isMounted) return;
        if (res.success) {
          setMetaAudienceOptions(mapSharedAudienceListToOptions(res.data));
        } else {
          enqueueSnackbar(res.error, { variant: "error" });
          setMetaAudienceOptions([]);
        }
      })
      .finally(() => {
        if (isMounted) setIsLoadingMetaAudienceOptions(false);
      });
    return () => {
      isMounted = false;
    };
  }, [isOpen, hotel_id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!effectiveCampaignId) {
      setToastType("error");
      setMessage("Aucune campagne sÃƒÂ©lectionnÃƒÂ©e: impossible de charger les dÃƒÂ©tails.");
      setSnackbarOpen(true);
      return;
    }
    const url = `${APP_META_TRAVEL_ADS_API_ENDPOINT}/api/meta-ads/get-campaign-details/?campaign_id=${effectiveCampaignId}`;
    setDrawerOpen(true);
    axios
      .get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        const data = response?.data || {};
        const cs = data?.campaign_settings || {};
        const budget = cs?.budget || {};

        // Statut et devise
        setIsCampaignPaused(data?.campaign_status === "PAUSED");
        setCurrencySymbol(data?.currency_symbol || "Ã¢â€šÂ¬");

        // Nom de campagne et stratÃƒÂ©gie d'enchÃƒÂ¨res
        setCampaignName(cs?.campaign_name || "");
        // Pick the first audience entry, supporting both legacy object and new array shapes.
        const audEntry = Array.isArray(data?.campaign_audience)
          ? data.campaign_audience?.[0]
          : data?.campaign_audience;
        if (audEntry?.source_type === "SHARED") {
          setSelectedObjective("META_AUDIENCE");
          setSelectedMetaAudience(
            audEntry?.shared_audience_id != null
              ? String(audEntry.shared_audience_id)
              : null,
          );
          // Load multi-audience selections
          if (Array.isArray(audEntry?.shared_audience_ids) && audEntry.shared_audience_ids.length > 0) {
            setSelectedIncludeAudiences(audEntry.shared_audience_ids.map(String));
          } else if (audEntry?.shared_audience_id != null) {
            setSelectedIncludeAudiences([String(audEntry.shared_audience_id)]);
          }
          if (Array.isArray(audEntry?.excluded_audience_ids) && audEntry.excluded_audience_ids.length > 0) {
            setSelectedExcludeAudiences(audEntry.excluded_audience_ids.map(String));
          }
        } else {
          setSelectedObjective(audEntry?.audience_type);
          // Load include audiences for PROSPECTING (from junction table)
          if (Array.isArray(audEntry?.shared_audience_ids) && audEntry.shared_audience_ids.length > 0) {
            setSelectedIncludeAudiences(audEntry.shared_audience_ids.map(String));
          }
        }
        const biddingOption = cs?.bidding_strategy?.bidding_strategy_option || "";
        setBiddingStrategy(biddingOption);
        setCampaignBudget(cs?.bidding_strategy?.campaign_target_budget ?? "");

        // Budget
        setTargetValue(budget?.default_budget ?? 0);
        setDailyBudgetCap(Boolean(budget?.is_daily_budget));
        const monthlyBudgets = Object.keys(budget).reduce((acc, key, idx) => {
          if (key.includes("budget_month")) {
            const monthKey = Object.keys(monthlyBudget)[idx] || Object.keys(monthlyBudget)[0];
            acc[monthKey] = budget?.[key] ?? 0;
          }
          return acc;
        }, {});
        if (Object.keys(monthlyBudgets).length > 0) setMonthlyBudget(monthlyBudgets);

        // Langues (JSON string -> [{id_lang, language_name}])
        const rawLang = cs?.campaign_lang?.[0];
        let parsedLanguages = [];
        if (rawLang) {
          try {
            const langObj = typeof rawLang === 'string' ? JSON.parse(rawLang) : rawLang; // { "6": "English (US)" }
            parsedLanguages = Object.entries(langObj).map(([id, name]) => ({ id_lang: String(id), language_name: name }));
            console.log("parsedLanguages:", parsedLanguages);
          } catch (e) {
            console.error("Invalid campaign_lang format", e);
          }
        }
        setSelectedLanguage(parsedLanguages);

        // Points de vente (JSON -> [codes]) attendu par TargetLocations
          const rawPOS = cs?.point_of_sales?.[0];
          if (rawPOS) {
              try {
                  const parsedPOS = typeof rawPOS === "string" ? JSON.parse(rawPOS) : rawPOS;

                  const codes = Array.isArray(parsedPOS)
                      ? parsedPOS.map(String)
                      : Object.keys(parsedPOS).map(String);

                  setSelectedCountries(codes);
              } catch (e) {
                  console.error("Invalid point_of_sales format", e);
                  setSelectedCountries([]);
              }
          } else {
              setSelectedCountries([]);
          }


        // Placements (plateformes / devices)
        const rawPlatform = cs?.placement_platform?.[0];
        const rawDevice = cs?.placement_device?.[0];
        let placement_platform = [];
        let placement_device = [];
        try {
          if (rawPlatform) {
            const platformObj = typeof rawPlatform === 'string' ? JSON.parse(rawPlatform) : rawPlatform; // {"1":"Facebook"}
            placement_platform = Object.keys(platformObj).map(String);
          }
          if (rawDevice) {
            const deviceObj = typeof rawDevice === 'string' ? JSON.parse(rawDevice) : rawDevice; // {"1":"Mobile"}
            placement_device = Object.keys(deviceObj).map(String);
          }
        } catch (e) {
          console.error("Invalid placement format", e);
        }
        setPlacementDetails({ placement_platform, placement_device });
        // Attributs d'attribution
        setClickThroughValue(cs?.attr_click_through ?? 7);
        setViewThroughValue(cs?.attr_view_through ?? 1);

        // Ads creative (liste de cartes)
        const creatives = Array.isArray(data?.campaign_ads_creative)
          ? data.campaign_ads_creative.map((item, idx) => ({ id: idx + 1, ...item }))
          : [{ id: 1 }];
        setAdsCreativeCards(creatives);

        // Selected hotels (multi-hotel mode)
        if (Array.isArray(data?.selected_hotels) && data.selected_hotels.length > 0) {
          setSelectedHotels(data.selected_hotels);
          setLockedHotels(data.selected_hotels.map(String));
        } else {
          const initialHotels = data?.campaign_hotel_id ? [String(data.campaign_hotel_id)] : [];
          setSelectedHotels(initialHotels);
          setLockedHotels(initialHotels.map(String));
        }

        // Audience (RETARGETING/PROSPECTING/META_AUDIENCE)
        const aud = audEntry || {};
        setAgeRange({
          min: aud?.age_min != null ? String(aud.age_min) : "",
          max: aud?.age_max != null ? String(aud.age_max) : "",
        });
        setSelectedGender((aud?.gender || "ALL").toUpperCase());
        const bws = aud?.booking_window_start;
        const bwe = aud?.booking_window_end;
        setIsBookingWindowEnabled(bws != null || bwe != null);
        setBookingWindowRange({
          booking_window_start: bws != null ? String(bws) : "",
          booking_window_end: bwe != null ? String(bwe) : "",
        });
        setAudienceRules(
          Array.isArray(aud?.rules)
            ? aud.rules.map((r, idx) => ({
                id: idx + 1,
                type: r?.type ? String(r.type).toUpperCase() : "INCLUDE",
                source: r?.source || "",
                operator: r?.operator || "",
                daysRange: {
                  days_from: r?.days_from ?? "",
                  days_to: r?.days_to ?? "",
                },
              }))
            : []
        );
      })
      .catch((err) => {
          console.error(err?.message);
        }).finally(() => {
          setDrawerOpen(false)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCampaignId, token, isOpen]);

  const handleToggle = (_, checked) => {
    setIsCampaignPaused((prev) => !prev);
    setDrawerOpen(true);
    const payload = {
      campaign_id: effectiveCampaignId,
      campaign_status: checked ? "PAUSED" : "ENABLED",
    };
    axios.put(
        `${APP_META_TRAVEL_ADS_API_ENDPOINT}/api/meta-ads/edit-campaign/update-status/`,
        null,
        {
          params: payload,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
    )
      .then((_res) => {
        setToastType("success");
        setMessage("Campaign updated successfully!");
        if (onStatusChange) onStatusChange();
        setSnackbarOpen(true);
      })
      .catch((err) => {
        setToastType("error");
        setMessage(err?.message || "Something went wrong!");
        setSnackbarOpen(true);
      }).finally(() => {
        setDrawerOpen(false);
    });
  };

  const handleNext = async (currentPanel) => {
    if (currentPanel === "settings") {
      setExpandedPanel("audiences");
      setVisitedPanels((prev) => [...prev, "audiences"]);
    } else if (currentPanel === "audiences") {
      setExpandedPanel("adsCreative");
      setVisitedPanels((prev) => [...prev, "adsCreative"]);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);


  const onSaveSettings = async () => {
    await saveCampaignSettings({
      effectiveCampaignId,
      campaignName,
      selectedObjective,
      biddingStrategy,
      campaignBudget,
      placementDetails,
      targetValue,
      monthlyBudget,
      selectedLanguage,
      selectedCountries,
      clickThroughValue,
      viewThroughValue,
      setDrawerOpen,
      onClose
    });
  };


  const onSaveAudience = async () => {
    await saveAudience({
      effectiveCampaignId,
      selectedObjective,
      ageRange,
      selectedGender,
      bookingWindowRange,
      audienceRules,
      selectedMetaAudience,
      selectedIncludeAudiences,
      selectedExcludeAudiences,
      setDrawerOpen,
      onClose
    });
  };

  const onSaveAdsCreative = async () => {
    await saveAdsCreative({
      effectiveCampaignId,
      adsCreativeCards,
      setDrawerOpen,
      onClose
    });
  };

  return (
    <>
      {isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1100,
          }}
        />
      )}
      <Box className={`editCampaignPanel ${isOpen ? "open" : ""}`}>
        <Box
          className="header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={onClose} className="closeButton">
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="body1" className="title">
              Edit Campaign
            </Typography>
          </Box>
          {/* Toggle Button */}
          <Box style={{ display: "flex", alignItems: "center" }}>
            <Switch
              checked={isCampaignPaused}
              onChange={handleToggle}
              color="primary"
              inputProps={{ "aria-label": "Pause Campaign Toggle" }}
            />
            <Typography variant="body2" className="pause-toggle-title">
              Pause Campaign
            </Typography>
          </Box>
        </Box>
        <Box className="content">
          <MetaTravelAdsCampaignEditor
            onClose={onClose}
            expandedPanel={expandedPanel}
            setExpandedPanel={setExpandedPanel}
            selectedObjective={selectedObjective}
            setSelectedObjective={setSelectedObjective}
            visitedPanels={visitedPanels}
            handleNext={handleNext}
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            targetValue={targetValue}
            setTargetValue={setTargetValue}
            biddingStrategy={biddingStrategy}
            setBiddingStrategy={setBiddingStrategy}
            campaignName={campaignName}
            setCampaignName={setCampaignName}
            campaignBudget={campaignBudget}
            setCampaignBudget={setCampaignBudget}
            monthlyBudget={monthlyBudget}
            setMonthlyBudget={setMonthlyBudget}
            dailyBudgetCap={dailyBudgetCap}
            setDailyBudgetCap={setDailyBudgetCap}
            currencySymbol={currencySymbol}
            campaignId={campaignId}
            // Nouveaux props pour placements et crÃƒÂ©atifs
            placementDetails={placementDetails}
            setPlacementDetails={setPlacementDetails}
            adsCreativeCards={adsCreativeCards}
            setAdsCreativeCards={setAdsCreativeCards}
            hotelId={hotel_id?.[0]}
            // Nouveaux props pour Audiences
            ageRange={ageRange}
            setAgeRange={setAgeRange}
            selectedGender={selectedGender}
            setSelectedGender={setSelectedGender}
            isBookingWindowEnabled={isBookingWindowEnabled}
            setIsBookingWindowEnabled={setIsBookingWindowEnabled}
            bookingWindowRange={bookingWindowRange}
            setBookingWindowRange={setBookingWindowRange}
            rules={audienceRules}
            setRules={setAudienceRules}
            clickThroughValue={clickThroughValue}
            setClickThroughValue={setClickThroughValue}
            viewThroughValue={viewThroughValue}
            setViewThroughValue={setViewThroughValue}
            onSaveSettings={onSaveSettings}
            onSaveAudience={onSaveAudience}
            onSaveAdsCreative={onSaveAdsCreative}
            selectedMetaAudience={selectedMetaAudience}
            setSelectedMetaAudience={setSelectedMetaAudience}
            selectedIncludeAudiences={selectedIncludeAudiences}
            setSelectedIncludeAudiences={setSelectedIncludeAudiences}
            selectedExcludeAudiences={selectedExcludeAudiences}
            setSelectedExcludeAudiences={setSelectedExcludeAudiences}
            metaAudienceOptions={metaAudienceOptions}
            isLoadingMetaAudienceOptions={isLoadingMetaAudienceOptions}
            isSharedAudienceEnabled={isSharedAudienceEnabled}
            isMultiHotelEnabled={isMultiHotelEnabled}
            selectedHotels={selectedHotels}
            setSelectedHotels={setSelectedHotels}
            lockedHotels={lockedHotels}
          />
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={7000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={toastType}
            sx={{ width: "100%" }}
          >
            {message}
          </Alert>
        </Snackbar>
        <Drawer
            anchor="top"
            open={drawerOpen}
            PaperProps={{
              sx: {
                background: "transparent"
              },
            }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
            <CircularProgress size={60} color="primary" />
          </Box>
        </Drawer>
      </Box>
    </>
  );
};

export default EditCampaignPanel;
